/**
 * WHETHER A BLOCK CAN STILL REACH ITS PASS STATE — the question All Correct is really asking.
 *
 * `everyBlockPassed` holds when every block that fired carries a POSITIVE trigger, and
 * `DEFAULT_FLOW_CONFIG` advances a task on All Correct alone. So a block whose pass state can
 * never fire does not merely fail to be green: it stops the TASK, on every submission, with no
 * rung below All Correct that routes anywhere else. Reported by the owner as All Correct simply
 * never firing, with nothing on screen saying why.
 *
 * ## It only answers where it can PROVE the answer
 *
 * The reachability modules below already decide this exactly, per block, and the editors already
 * draw an `UnreachableChip` from them — this is the same question asked from outside a block
 * rather than a second derivation of it. Where no module can answer, this says "reachable",
 * because the two mistakes are not equal: a missed case leaves today's silence, while a false
 * alarm puts a warning on a task that is perfectly well authored.
 *
 * That boundary is a real one rather than a shortcut. A pass state is starved by TARGETED states
 * covering the whole answer space, which can only happen where the space is finite and small —
 * the single-pick family. Keyword and FITB always keep "none of these" reachable, and a range or
 * a sensor reading cannot be enumerated at all.
 *
 * `isReachable` is deliberately NOT used here: it answers `false` for a trigger it has never
 * heard of, which is right for a card asking about itself and wrong for a sweep asking about
 * every block in a task — an unlisted trigger would read as a dead end on sight.
 */
import { positiveTriggerFor } from './grading-mode.js';
import { dropdownReachability } from './dropdown-reachability.js';
import { mcqReachability } from './mcq-reachability.js';
import { scaleReachability } from './scale-reachability.js';
/**
 * THE CAUSES THAT MEAN A TEACHER AUTHORED A DEAD END, as against one they have not finished.
 *
 * An EMPTY single-pick block reports its pass state unreachable with `nothing-to-pick`, which is
 * true and is not a fault: a block is empty the instant it is created, and warning about it is
 * the same mistake `getCardProblem`'s `settled` flag exists to avoid — that is not information,
 * it is the definition of new. An allowlist rather than a denylist so a cause added later is
 * silent until someone decides it belongs here.
 */
const AUTHORED_DEAD_END = new Set([
    // Survey with every answer covered by a targeted state — the case this was written for.
    'fully-covered',
    // Graded with nothing roled correct: `correct` can never fire, so All Correct is just as dead.
    'no-correct-option',
    // Several correct options where only one can be picked.
    'multiple-correct-single-select',
    // FITB Correct is a conjunction, and one blank with no typeable correct word kills it.
    'blank-missing-correct-word',
]);
const arr = (v) => (Array.isArray(v) ? v : []);
/**
 * `feedback` is a RECORD holding `states`, not a list of them.
 *
 * Read wrongly here once, and the `as never` casts below were what let it compile — the
 * reachability modules iterate `d.feedback.states`, so an array arrived as an object with no
 * `.states` and threw inside the module rather than at the call. Normalised in one place so the
 * three call sites cannot each get it half right.
 */
const feedbackOf = (v) => {
    const states = v?.states;
    return { states: (Array.isArray(states) ? states : []) };
};
/**
 * The reach of the one block, or null where this cannot say.
 *
 * Each module is handed the same two things it is handed in the editor — the grading-relevant
 * slice of the data, and the block's own list of pickable items — so the answer here and the chip
 * over there come from one computation and cannot disagree.
 */
function reachOf(blockType, data) {
    const mode = data.gradingMode;
    if (!mode)
        return null;
    const feedback = feedbackOf(data.feedback);
    switch (blockType) {
        /*
         * The casts are narrowed to the ARGUMENT each module declares rather than written as `as
         * never`, which is what let a malformed `feedback` through the compiler and into a throw
         * inside the module. `Parameters<...>` keeps them honest: change a module's signature and
         * these stop compiling instead of failing at the call.
         */
        case 'mcq':
            return mcqReachability({ allowMultiple: data.allowMultiple === true, gradingMode: mode, feedback }, arr(data.options));
        case 'dropdown':
            return dropdownReachability({ gradingMode: mode, feedback }, arr(data.options));
        case 'scale':
            return scaleReachability({ gradingMode: mode, feedback }, arr(data.values));
        default:
            return null;
    }
}
/**
 * Whether this block's pass state is provably unable to fire.
 *
 * False for every block this cannot prove, including every block type with no reachability module
 * — see the note above on why silence is the safe answer.
 */
export function positiveStateStarved(block) {
    const data = (block.data ?? {});
    const mode = data.gradingMode;
    if (!mode)
        return false;
    return starvedByReach(reachOf(block.type, data), mode);
}
/**
 * The decision itself, over a reach that has already been computed.
 *
 * Separate and exported so the two silences below can be tested directly. Neither is reachable
 * through `positiveStateStarved` today — every module lists all five triggers — and an untestable
 * guard is one that quietly stops working.
 */
export function starvedByReach(reach, mode) {
    return starvedCauseByReach(reach, mode) !== null;
}
/**
 * WHY it cannot pass, or null if it can — the same decision, keeping what it decided on.
 *
 * The cause is what a teacher can act on. "No reachable Any Answer" names a trigger, and names
 * the wrong one as soon as the block is graded; "nothing is marked correct" names the edit. Each
 * of the four allowed causes has its own remedy, so the warning that reports them has to carry
 * which one fired rather than assume.
 */
export function starvedCauseByReach(reach, mode) {
    // ABSENT means "this module does not describe that trigger", not "it cannot fire". Reading it
    // as a dead end would put a warning on every block a future module stopped describing.
    const state = reach?.states.find((s) => s.trigger === positiveTriggerFor(mode));
    if (!state || state.reachable)
        return null;
    return state.cause != null && AUTHORED_DEAD_END.has(state.cause) ? state.cause : null;
}
/**
 * The blocks in a task that can no longer pass, which is what makes All Correct unreachable.
 *
 * A list rather than a boolean so a caller can name them. Empty is the ordinary case and also the
 * answer for a task this cannot reason about.
 */
export function blocksBlockingAllCorrect(blocks) {
    return starvedBlockCauses(blocks).map((b) => b.id);
}
/**
 * The same sweep, keeping each block's cause.
 *
 * The id-only form above is kept because two callers only need to know THAT a task is blocked;
 * this is for the one that has to say why.
 */
export function starvedBlockCauses(blocks) {
    const out = [];
    for (const block of blocks) {
        const data = (block.data ?? {});
        const mode = data.gradingMode;
        if (!mode)
            continue;
        const cause = starvedCauseByReach(reachOf(block.type, data), mode);
        if (cause)
            out.push({ id: block.id ?? '', cause });
    }
    return out;
}
//# sourceMappingURL=positive-reachability.js.map